import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import {
  applyLoadedEducationNotAttendingSchool,
  finishTreatmentPlanV3Mutation,
  startTreatmentPlanV3Mutation,
} from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { IChildBackground, IChildSchedule } from '../../../../pages/common';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  ITreatmentPlanV3ChildPage,
  UpdateTreatmentPlanV3ChildInfoPayload,
  CreateTreatmentPlanV3ChildBackgroundPayload,
  DeleteTreatmentPlanV3ChildBackgroundPayload,
  CreateTreatmentPlanV3ChildSchedulePayload,
  DeleteTreatmentPlanV3ChildSchedulePayload,
  TreatmentPlanV3ChildThunks,
  ChildMutationAction,
} from './types';

export function addTreatmentPlanV3ChildCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  addGetTreatmentPlanV3ChildPageCases(builder, thunks);
  addUpdateTreatmentPlanV3ChildInfoCases(builder, thunks);
  addCreateTreatmentPlanV3ChildBackgroundCases(builder, thunks);
  addDeleteTreatmentPlanV3ChildBackgroundCases(builder, thunks);
  addCreateTreatmentPlanV3ChildScheduleCases(builder, thunks);
  addDeleteTreatmentPlanV3ChildScheduleCases(builder, thunks);
}

function isForLoadedChildPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.childPageTreatmentPlanId;
}

function addGetTreatmentPlanV3ChildPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  const { getTreatmentPlanV3ChildPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3ChildPage.pending, (state, action) => {
      state.childPage = null;
      state.childPageLoading = true;
      state.childPageError = null;
      state.childPageRequestId = action.meta.requestId;
      state.childPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3ChildPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.childPageRequestId) return;
      state.childPageLoading = false;
      state.childPageError = null;
      state.childPage = action.payload;
      applyLoadedEducationNotAttendingSchool(state, action.meta.arg.treatmentPlanId);
    })
    .addCase(getTreatmentPlanV3ChildPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.childPageRequestId) return;
      state.childPageLoading = false;
      state.childPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateTreatmentPlanV3ChildInfoCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  const { updateTreatmentPlanV3ChildInfo } = thunks;
  const flag = 'updateChildInfoLoading';

  builder
    .addCase(updateTreatmentPlanV3ChildInfo.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3ChildInfo.fulfilled,
      (
        state,
        action: ChildMutationAction<
          ITreatmentPlanV3ChildPage,
          UpdateTreatmentPlanV3ChildInfoPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedChildPage(state, action)) return;
        state.childPage = action.payload;
        applyLoadedEducationNotAttendingSchool(state, action.meta.arg.treatmentPlanId);
      },
    )
    .addCase(updateTreatmentPlanV3ChildInfo.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedChildPage(state, action)) return;
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3ChildBackgroundCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  const { createTreatmentPlanV3ChildBackground } = thunks;
  const flag = 'createChildBackgroundLoading';

  builder
    .addCase(createTreatmentPlanV3ChildBackground.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3ChildBackground.fulfilled,
      (
        state,
        action: ChildMutationAction<
          IChildBackground,
          CreateTreatmentPlanV3ChildBackgroundPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedChildPage(state, action) || !state.childPage) return;
        state.childPage.backgrounds.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3ChildBackground.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedChildPage(state, action)) return;
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3ChildBackgroundCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  const { deleteTreatmentPlanV3ChildBackground } = thunks;
  const flag = 'deleteChildBackgroundLoading';

  builder
    .addCase(deleteTreatmentPlanV3ChildBackground.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3ChildBackground.fulfilled,
      (
        state,
        action: ChildMutationAction<void, DeleteTreatmentPlanV3ChildBackgroundPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedChildPage(state, action) || !state.childPage) return;
        state.childPage.backgrounds = state.childPage.backgrounds.filter(
          (row) => row.id !== action.meta.arg.backgroundId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3ChildBackground.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedChildPage(state, action)) return;
      state.error = action.error;
    });
}

function addCreateTreatmentPlanV3ChildScheduleCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  const { createTreatmentPlanV3ChildSchedule } = thunks;
  const flag = 'createChildScheduleLoading';

  builder
    .addCase(createTreatmentPlanV3ChildSchedule.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3ChildSchedule.fulfilled,
      (
        state,
        action: ChildMutationAction<IChildSchedule, CreateTreatmentPlanV3ChildSchedulePayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedChildPage(state, action) || !state.childPage) return;
        state.childPage.schedule.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3ChildSchedule.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedChildPage(state, action)) return;
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3ChildScheduleCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3ChildThunks,
) {
  const { deleteTreatmentPlanV3ChildSchedule } = thunks;
  const flag = 'deleteChildScheduleLoading';

  builder
    .addCase(deleteTreatmentPlanV3ChildSchedule.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3ChildSchedule.fulfilled,
      (
        state,
        action: ChildMutationAction<void, DeleteTreatmentPlanV3ChildSchedulePayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedChildPage(state, action) || !state.childPage) return;
        state.childPage.schedule = state.childPage.schedule.filter(
          (row) => row.id !== action.meta.arg.scheduleId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3ChildSchedule.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedChildPage(state, action)) return;
      state.error = action.error;
    });
}
